/**
 * 时间口径统一工具（玩家端单源）
 *
 * 口径以平台仓 contracts/time/vectors.v1.json（本仓副本 app/utils/__tests__/vectors.v1.json）为准：
 * - 时刻对外恒为 UTC、3 位毫秒、Z 结尾（2026-09-28T02:30:00.000Z）；解析只收 RFC3339，宽进严出。
 * - 玩家端所有时刻按**设备本地时区**显示；「今日 / 本月 / 每日重置」这类站点日界按**站点时区**
 *   （只来自 GET /bootstrap 的 site.timezone）计算。
 * - 区间一律左闭右开 [from, to)；墙钟换时刻按 Temporal compatible 消歧义（重叠取较早、空档顺延）。
 * - 周从周一开始（ISO 8601），周期号 2026-W39 用 ISO 周年。
 *
 * 本文件**自包含**：不 import 任何模块，只用可擦除的 TS 语法，Node 24 可直接 `node --test` 跑向量。
 * 页面禁止自行 new Date(<参数>) / Date.parse / toISOString().slice / 直接引入 dayjs（ESLint 拦截），
 * 一律走这里的函数，或 <LocalTime> / <CountdownText> 组件。
 */

// ============================================================================
// 基础常量与类型
// ============================================================================

export const SECOND_MS = 1000
export const MINUTE_MS = 60 * SECOND_MS
export const HOUR_MS = 60 * MINUTE_MS
export const DAY_MS = 24 * HOUR_MS

/** 界面语言：中文 / 英文两套格式 */
export type TimeLocale = 'zh' | 'en'

/** 公历钟点（年月日必填，其余缺省 0） */
export interface LocalInput {
  year: number
  month: number
  day: number
  hour?: number
  minute?: number
  second?: number
  millisecond?: number
}

/** 某时刻在某时区的钟点与偏移 */
export interface LocalInfo {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  millisecond: number
  /** 该时刻在该时区相对 UTC 的偏移（秒，东正西负） */
  offsetSeconds: number
}

/** 左闭右开的时刻区间（毫秒） */
export interface Bounds {
  from: number
  to: number
}

/** 周区间，附 ISO 周期号（如 2026-W39） */
export interface WeekBounds extends Bounds {
  key: string
}

/** 左闭右开的业务日区间：from 含、to 不含（YYYY-MM-DD） */
export interface DateSpan {
  from: string
  to: string
}

/** 平台统一的 time_range 键（各接口只允许自己的子集） */
export type RangeKey =
  | 'today'
  | 'yesterday'
  | 'last_7d'
  | 'last_30d'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'

export const RANGE_KEYS: readonly RangeKey[] = [
  'today',
  'yesterday',
  'last_7d',
  'last_30d',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
]

const pad = (n: number, width = 2): string => String(n).padStart(width, '0')

// ============================================================================
// 公历算术（不经 Date，任意年份可用；Howard Hinnant days_from_civil / civil_from_days）
// ============================================================================

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
}

function daysInMonthOf(y: number, m: number): number {
  if (m === 2) return isLeapYear(y) ? 29 : 28
  return m === 4 || m === 6 || m === 9 || m === 11 ? 30 : 31
}

/** 公历日期 → 自 1970-01-01 起的天数 */
function daysFromCivil(y: number, m: number, d: number): number {
  const yy = m <= 2 ? y - 1 : y
  const era = Math.floor(yy / 400)
  const yoe = yy - era * 400
  const mp = (m + 9) % 12
  const doy = Math.floor((153 * mp + 2) / 5) + d - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy
  return era * 146097 + doe - 719468
}

/** 自 1970-01-01 起的天数 → 公历 [年, 月, 日] */
function civilFromDays(days: number): [number, number, number] {
  const z = days + 719468
  const era = Math.floor(z / 146097)
  const doe = z - era * 146097
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  )
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100))
  const mp = Math.floor((5 * doy + 2) / 153)
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1
  const m = mp < 10 ? mp + 3 : mp - 9
  const y = yoe + era * 400 + (m <= 2 ? 1 : 0)
  return [y, m, d]
}

/** 把公历钟点当作 UTC 换算成毫秒 */
function civilToUtcMs(y: number, mo: number, d: number, h = 0, mi = 0, s = 0, ms = 0): number {
  return daysFromCivil(y, mo, d) * DAY_MS + h * HOUR_MS + mi * MINUTE_MS + s * SECOND_MS + ms
}

/** 毫秒 → UTC 公历钟点 */
function utcParts(ms: number): Omit<LocalInfo, 'offsetSeconds'> {
  const t = Math.floor(ms)
  const days = Math.floor(t / DAY_MS)
  let rest = t - days * DAY_MS
  const [year, month, day] = civilFromDays(days)
  const hour = Math.floor(rest / HOUR_MS)
  rest -= hour * HOUR_MS
  const minute = Math.floor(rest / MINUTE_MS)
  rest -= minute * MINUTE_MS
  const second = Math.floor(rest / SECOND_MS)
  const millisecond = rest - second * SECOND_MS
  return { year, month, day, hour, minute, second, millisecond }
}

const fmtYmd = (y: number, m: number, d: number): string => `${pad(y, 4)}-${pad(m)}-${pad(d)}`

// ============================================================================
// 时刻：解析与输出（规范 2.1 / 2.2）
// ============================================================================

const INSTANT_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(?:Z|([+-])([01]\d|2[0-3]):([0-5]\d))$/

function reportInvalidInstant(input: string): void {
  if (import.meta.dev) {
    console.error(
      `[time] 非法时刻串（只收带时区的 RFC3339，如 2026-09-28T02:30:00.000Z）：${JSON.stringify(input)}`,
    )
  }
}

/**
 * 解析对外时刻串 → 毫秒（UTC epoch），不合法返回 null。
 * 只收 RFC3339：大写 T、秒必填、小数 1–9 位（多余精度截断到毫秒）、Z 或 ±HH:MM；
 * 并做日历合法性校验（2 月 30 日、24 时、闰秒 60 秒都拒）。
 * null / undefined / 空串视为「缺席」，静默返回 null；其它非法串在 dev 下 console.error。
 */
export function parseInstant(input: unknown): number | null {
  if (input === null || input === undefined || input === '') return null
  if (typeof input !== 'string') {
    reportInvalidInstant(String(input))
    return null
  }
  const m = INSTANT_RE.exec(input)
  if (!m) {
    reportInvalidInstant(input)
    return null
  }
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  const hour = Number(m[4])
  const minute = Number(m[5])
  const second = Number(m[6])
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonthOf(year, month) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    reportInvalidInstant(input)
    return null
  }
  const frac = m[7] ?? ''
  const millisecond = frac ? Number(`${frac}00`.slice(0, 3)) : 0
  let offsetMs = 0
  if (m[8]) {
    const sign = m[8] === '-' ? -1 : 1
    offsetMs = sign * (Number(m[9]) * HOUR_MS + Number(m[10]) * MINUTE_MS)
  }
  return civilToUtcMs(year, month, day, hour, minute, second, millisecond) - offsetMs
}

/** 毫秒 → 对外时刻串：恒 UTC、恒 3 位毫秒、Z 结尾（多余精度截断） */
export function formatInstant(ms: number): string {
  if (!Number.isFinite(ms)) throw new RangeError(`formatInstant: 非法毫秒数 ${String(ms)}`)
  const p = utcParts(ms)
  if (p.year < 0 || p.year > 9999) {
    throw new RangeError(`formatInstant: 年份超出 0000–9999：${String(ms)}`)
  }
  return `${fmtYmd(p.year, p.month, p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}.${pad(p.millisecond, 3)}Z`
}

/** 规范化时刻串（宽进严出）；不合法返回 null */
export function normalizeInstant(input: unknown): string | null {
  const ms = parseInstant(input)
  return ms === null ? null : formatInstant(ms)
}

// ============================================================================
// 时区（规范 2.4）：只收 IANA 名，Intl 实例按时区缓存
// ============================================================================

const formatterCache = new Map<string, Intl.DateTimeFormat | null>()

function zoneFormatter(tz: string | undefined): Intl.DateTimeFormat | null {
  const key = tz ?? ''
  const cached = formatterCache.get(key)
  if (cached !== undefined) return cached
  let f: Intl.DateTimeFormat | null
  try {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    f = null
  }
  formatterCache.set(key, f)
  return f
}

/**
 * 时区名是否可用：只收 IANA 名。
 * 拒空串、前后空白、`Local`、以 +/- 开头的偏移写法（新版 Intl 会接受 +08:00，这里显式拒），
 * 其余交给 Intl 判定是否存在。
 */
export function isValidZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz === '' || tz.trim() !== tz) return false
  if (tz === 'Local' || tz.startsWith('+') || tz.startsWith('-')) return false
  return zoneFormatter(tz) !== null
}

/** 设备（运行环境）的 IANA 时区；取不到或不合法返回 null。服务端（TZ=UTC）调用无意义。 */
export function deviceZone(): string | null {
  try {
    const tz = new Intl.DateTimeFormat().resolvedOptions().timeZone
    return isValidZone(tz) ? tz : null
  } catch {
    return null
  }
}

/**
 * 时刻 → 某时区的钟点与偏移。tz 缺省 = 运行环境默认时区（只在客户端有意义）。
 * 偏移按 formatToParts 反推（与 contracts/time/tools/check.mjs 的 offsetSec 同法）。
 */
export function toLocal(ms: number, tz?: string): LocalInfo {
  if (!Number.isFinite(ms)) throw new RangeError(`toLocal: 非法毫秒数 ${String(ms)}`)
  if (tz !== undefined && !isValidZone(tz)) throw new RangeError(`toLocal: 非法时区 ${tz}`)
  const f = zoneFormatter(tz)
  if (!f) throw new RangeError(`toLocal: 非法时区 ${String(tz)}`)
  let year = 0
  let month = 0
  let day = 0
  let hour = 0
  let minute = 0
  let second = 0
  for (const part of f.formatToParts(ms)) {
    switch (part.type) {
      case 'year':
        year = Number(part.value)
        break
      case 'month':
        month = Number(part.value)
        break
      case 'day':
        day = Number(part.value)
        break
      case 'hour':
        hour = Number(part.value)
        break
      case 'minute':
        minute = Number(part.value)
        break
      case 'second':
        second = Number(part.value)
        break
    }
  }
  // 个别旧内核在 h23 下仍把午夜写成 24
  if (hour === 24) hour = 0
  const floorSec = Math.floor(ms / SECOND_MS) * SECOND_MS
  const offsetSeconds = Math.round(
    (civilToUtcMs(year, month, day, hour, minute, second) - floorSec) / SECOND_MS,
  )
  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    millisecond: Math.floor(ms) - floorSec,
    offsetSeconds,
  }
}

/** 某时刻在某时区的偏移（秒） */
export function offsetSeconds(ms: number, tz?: string): number {
  return toLocal(ms, tz).offsetSeconds
}

/** 偏移 → 标签：UTC+8、UTC-3、UTC+5:30、UTC+0（ASCII 连字符） */
export function offsetLabel(seconds: number): string {
  const sign = seconds < 0 ? '-' : '+'
  const abs = Math.abs(seconds)
  const h = Math.floor(abs / 3600)
  const m = Math.floor((abs % 3600) / 60)
  return m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${pad(m)}`
}

/** 偏移 → ±HH:MM */
export function offsetString(seconds: number): string {
  const sign = seconds < 0 ? '-' : '+'
  const abs = Math.abs(seconds)
  return `${sign}${pad(Math.floor(abs / 3600))}:${pad(Math.floor((abs % 3600) / 60))}`
}

/** 某时刻在某时区的偏移标签（如 UTC-3） */
export function zoneOffsetLabel(ms: number, tz: string): string {
  return offsetLabel(offsetSeconds(ms, tz))
}

/**
 * 墙钟 → 时刻（规范 2.5，Temporal compatible 消歧义）：
 * naive = 把墙钟当 UTC；o1/o2 = naive∓24h 处的偏移；c1 = naive−o1、c2 = naive−o2；
 * 候选有效 ⇔ 它在该时区的偏移恰等于 naive−候选。两个都有效取较早（重叠），
 * 一个有效取它，都无效（空档）取 c1（按跳变前的偏移解释，结果顺延空档长度）。
 */
export function fromLocal(input: LocalInput, tz: string): number {
  if (!isValidZone(tz)) throw new RangeError(`fromLocal: 非法时区 ${String(tz)}`)
  const naive = civilToUtcMs(
    input.year,
    input.month,
    input.day,
    input.hour ?? 0,
    input.minute ?? 0,
    input.second ?? 0,
    input.millisecond ?? 0,
  )
  const o1 = offsetSeconds(naive - DAY_MS, tz) * SECOND_MS
  const o2 = offsetSeconds(naive + DAY_MS, tz) * SECOND_MS
  const c1 = naive - o1
  const c2 = naive - o2
  const valid1 = offsetSeconds(c1, tz) * SECOND_MS === naive - c1
  const valid2 = offsetSeconds(c2, tz) * SECOND_MS === naive - c2
  if (valid1 && valid2) return Math.min(c1, c2)
  if (valid1) return c1
  if (valid2) return c2
  return c1
}

// ============================================================================
// 业务日 / 业务月 / 周期号（规范 2.3 / 2.6）
// ============================================================================

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTH_RE = /^(\d{4})-(\d{2})$/

/** 业务日 YYYY-MM-DD，必须是真实日期 */
export function isValidDate(s: unknown): s is string {
  if (typeof s !== 'string') return false
  const m = DATE_RE.exec(s)
  if (!m) return false
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  return mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonthOf(y, mo)
}

/** 业务月 YYYY-MM */
export function isValidMonth(s: unknown): s is string {
  if (typeof s !== 'string') return false
  const m = MONTH_RE.exec(s)
  if (!m) return false
  const mo = Number(m[2])
  return mo >= 1 && mo <= 12
}

function dateToDays(date: string): number {
  if (!isValidDate(date)) throw new RangeError(`非法业务日：${String(date)}`)
  return daysFromCivil(
    Number(date.slice(0, 4)),
    Number(date.slice(5, 7)),
    Number(date.slice(8, 10)),
  )
}

function daysToDate(days: number): string {
  const [y, m, d] = civilFromDays(days)
  return fmtYmd(y, m, d)
}

/** 业务日加减 n 天 */
export function addDays(date: string, n: number): string {
  return daysToDate(dateToDays(date) + n)
}

/** 两个业务日相差的天数（b − a） */
export function diffDays(a: string, b: string): number {
  return dateToDays(b) - dateToDays(a)
}

/** 业务月加减 n 个月 */
export function addMonths(month: string, n: number): string {
  if (!isValidMonth(month)) throw new RangeError(`非法业务月：${String(month)}`)
  const idx = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + n
  const y = Math.floor(idx / 12)
  return `${pad(y, 4)}-${pad(idx - y * 12 + 1)}`
}

/** 某业务月的天数 */
export function daysInMonth(month: string): number {
  if (!isValidMonth(month)) throw new RangeError(`非法业务月：${String(month)}`)
  return daysInMonthOf(Number(month.slice(0, 4)), Number(month.slice(5, 7)))
}

/** ISO 星期：1=周一 … 7=周日 */
export function isoWeekday(date: string): number {
  return ((((dateToDays(date) + 3) % 7) + 7) % 7) + 1
}

/** 日周期号：2026-09-27 */
export function dayKey(date: string): string {
  if (!isValidDate(date)) throw new RangeError(`非法业务日：${String(date)}`)
  return date
}

/** 月周期号：2026-09 */
export function monthKey(date: string): string {
  if (!isValidDate(date)) throw new RangeError(`非法业务日：${String(date)}`)
  return date.slice(0, 7)
}

/** 周周期号：2026-W39（ISO 周年：2026-12-31 与 2027-01-01 都是 2026-W53） */
export function weekKey(date: string): string {
  const days = dateToDays(date)
  const thursday = days - (isoWeekday(date) - 1) + 3
  const [isoYear] = civilFromDays(thursday)
  const week = Math.floor((thursday - daysFromCivil(isoYear, 1, 1)) / 7) + 1
  return `${pad(isoYear, 4)}-W${pad(week)}`
}

/** 某时刻在某时区的业务日（YYYY-MM-DD） */
export function localDate(ms: number, tz?: string): string {
  const p = toLocal(ms, tz)
  return fmtYmd(p.year, p.month, p.day)
}

/** 某时区「今天」（默认按服务端校正后的当前时刻） */
export function todayIn(tz?: string, now: number = serverNow()): string {
  return localDate(now, tz)
}

/** 业务日在某时区的起点：该日 00:00 墙钟按 2.5 换算（当天没有 00:00 时从跳变时刻开始） */
export function dayStart(date: string, tz: string): number {
  const days = dateToDays(date)
  const [year, month, day] = civilFromDays(days)
  return fromLocal({ year, month, day }, tz)
}

/** 日区间 [当日 00:00, 次日 00:00)；夏令时当天可能 23 或 25 小时 */
export function dayBounds(date: string, tz: string): Bounds {
  return { from: dayStart(date, tz), to: dayStart(addDays(date, 1), tz) }
}

/** 所在 ISO 周（周一开始）的区间与周期号 */
export function weekBounds(date: string, tz: string): WeekBounds {
  const monday = addDays(date, 1 - isoWeekday(date))
  return {
    from: dayStart(monday, tz),
    to: dayStart(addDays(monday, 7), tz),
    key: weekKey(date),
  }
}

/** 业务月区间 [1 号 00:00, 次月 1 号 00:00) */
export function monthBounds(month: string, tz: string): Bounds {
  return {
    from: dayStart(`${month}-01`, tz),
    to: dayStart(`${addMonths(month, 1)}-01`, tz),
  }
}

/**
 * time_range 键 → 业务日区间 [from, to)（from 含、to 不含）。
 * 上界一律是区间末日的次日（不是 now）：本周 = 整个 ISO 周、本月 = 整个自然月、近 7 日含今天共 7 天。
 * tz 只用来确定「今天」；缺省 = 运行环境默认时区（只在客户端有意义）。
 */
export function rangeDates(key: RangeKey, now: number, tz?: string): DateSpan {
  const today = localDate(now, tz)
  const monday = addDays(today, 1 - isoWeekday(today))
  const month = today.slice(0, 7)
  switch (key) {
    case 'today':
      return { from: today, to: addDays(today, 1) }
    case 'yesterday':
      return { from: addDays(today, -1), to: today }
    case 'last_7d':
      return { from: addDays(today, -6), to: addDays(today, 1) }
    case 'last_30d':
      return { from: addDays(today, -29), to: addDays(today, 1) }
    case 'this_week':
      return { from: monday, to: addDays(monday, 7) }
    case 'last_week':
      return { from: addDays(monday, -7), to: monday }
    case 'this_month':
      return { from: `${month}-01`, to: `${addMonths(month, 1)}-01` }
    case 'last_month':
      return { from: `${addMonths(month, -1)}-01`, to: `${month}-01` }
    default:
      throw new RangeError(`未知 time_range：${String(key)}`)
  }
}

/** time_range 键 → 时刻区间 [from, to) */
export function rangeBounds(key: RangeKey, now: number, tz: string): Bounds {
  const span = rangeDates(key, now, tz)
  return { from: dayStart(span.from, tz), to: dayStart(span.to, tz) }
}

// ============================================================================
// 每日时段 HH:mm-HH:mm（规范 2.3）
// ============================================================================

const WINDOW_RE = /^([01]\d|2[0-3]):([0-5]\d)-([01]\d|2[0-3]):([0-5]\d)$/

/** 解析后的每日时段：分钟数；allDay=true 表示空串（全天） */
export interface DailyWindow {
  from: number
  to: number
  allDay: boolean
}

/** 解析每日时段；空串 = 全天；不合法（格式错、起止相同）返回 null */
export function parseWindow(window: string): DailyWindow | null {
  if (window === '') return { from: 0, to: 0, allDay: true }
  const m = WINDOW_RE.exec(window)
  if (!m) return null
  const from = Number(m[1]) * 60 + Number(m[2])
  const to = Number(m[3]) * 60 + Number(m[4])
  if (from === to) return null
  return { from, to, allDay: false }
}

/**
 * 时段是否开放：按该时区当地钟点、精确到分钟；起点含、终点不含；起点大于终点表示跨零点。
 * 时段不合法时按关闭处理（fail-closed）。
 */
export function windowOpen(window: string, ms: number, tz: string): boolean {
  const w = parseWindow(window)
  if (!w) return false
  if (w.allDay) return true
  const p = toLocal(ms, tz)
  const minute = p.hour * 60 + p.minute
  return w.from < w.to ? minute >= w.from && minute < w.to : minute >= w.from || minute < w.to
}

/** 时段展示串：09:00–23:00（en dash）；空串原样返回 */
export function formatWindow(window: string): string {
  const w = parseWindow(window)
  if (!w || w.allDay) return ''
  const hm = (v: number) => `${pad(Math.floor(v / 60))}:${pad(v % 60)}`
  return `${hm(w.from)}–${hm(w.to)}`
}

// ============================================================================
// 服务端时钟：读响应头 X-Server-Time，按往返最短的样本维护时差
// ============================================================================

interface ClockSample {
  /** 服务端时刻 − 设备时刻（毫秒） */
  offset: number
  /** 样本不确定度（往返耗时；Date 头退化样本另加 1 秒） */
  uncertainty: number
  /** 样本采集时的设备时刻 */
  at: number
}

/** 最优样本的有效期：超过就接受新样本（哪怕往返更长），防止设备时钟漂移后一直沿用旧差值 */
const CLOCK_SAMPLE_TTL_MS = 5 * MINUTE_MS

let bestSample: ClockSample | null = null

/** 一次响应的时钟观测 */
export interface ServerClockObservation {
  /** 响应头 X-Server-Time（2.1 格式） */
  serverTime?: string | null
  /** 响应头 Date（读不到 X-Server-Time 时的退路，精度 1 秒） */
  date?: string | null
  /** 发请求时的设备时刻 */
  sentAt: number
  /** 收到响应时的设备时刻 */
  receivedAt: number
}

/** 记录一次时钟样本；两种头都读不到时忽略 */
export function recordServerClock(obs: ServerClockObservation): void {
  const rtt = Math.max(0, obs.receivedAt - obs.sentAt)
  let serverMs = parseInstant(obs.serverTime ?? null)
  let uncertainty = rtt
  if (serverMs === null) {
    const dateMs = parseHttpDate(obs.date ?? null)
    if (dateMs === null) return
    // Date 头截到秒：真实时刻落在 [date, date+1s)，取中点，不确定度多算 1 秒
    serverMs = dateMs + 500
    uncertainty = rtt + SECOND_MS
  }
  const sample: ClockSample = {
    offset: serverMs + rtt / 2 - obs.receivedAt,
    uncertainty,
    at: obs.receivedAt,
  }
  const best = bestSample
  if (
    !best ||
    sample.uncertainty <= best.uncertainty ||
    sample.at - best.at > CLOCK_SAMPLE_TTL_MS ||
    sample.at < best.at ||
    // 两个样本互相矛盾：设备时钟被改过，旧差值作废
    Math.abs(sample.offset - best.offset) > (sample.uncertainty + best.uncertainty) / 2 + SECOND_MS
  ) {
    bestSample = sample
  }
}

/** 当前采用的时差（服务端 − 设备，毫秒）；尚无样本时为 0 */
export function serverClockOffset(): number {
  return bestSample ? Math.round(bestSample.offset) : 0
}

/** 服务端校正后的当前时刻（毫秒）；尚无样本时退回设备时钟 */
export function serverNow(): number {
  return Date.now() + serverClockOffset()
}

/** 清空时钟样本（测试用） */
export function resetServerClock(): void {
  bestSample = null
}

const MONTHS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

const MONTHS_EN_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

const HTTP_DATE_RE =
  /^(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun), (\d{2}) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{4}) (\d{2}):(\d{2}):(\d{2}) GMT$/

/** 解析 HTTP 日期（IMF-fixdate，如 Sun, 28 Sep 2026 02:30:00 GMT）→ 毫秒；不合法返回 null */
export function parseHttpDate(value: string | null | undefined): number | null {
  if (!value) return null
  const m = HTTP_DATE_RE.exec(value.trim())
  if (!m) return null
  const day = Number(m[1])
  const month = MONTHS_EN.indexOf(m[2] as (typeof MONTHS_EN)[number]) + 1
  const year = Number(m[3])
  const hour = Number(m[4])
  const minute = Number(m[5])
  const second = Number(m[6])
  if (
    month < 1 ||
    day < 1 ||
    day > daysInMonthOf(year, month) ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null
  }
  return civilToUtcMs(year, month, day, hour, minute, second)
}

/**
 * 解析 Retry-After（秒数或 HTTP 日期）→ 剩余秒数；缺席或不合法返回 null。
 * HTTP 日期按 now（服务端校正后的时刻）换算，向上取整，不为负。
 */
export function parseRetryAfter(
  value: string | null | undefined,
  now: number = serverNow(),
): number | null {
  if (value === null || value === undefined) return null
  const v = value.trim()
  if (/^\d+$/.test(v)) return Number(v)
  const at = parseHttpDate(v)
  if (at === null) return null
  return Math.max(0, Math.ceil((at - now) / SECOND_MS))
}

/** 锁定 / 频控响应里「还要等多久」的三处来源（平台 RetryInfo 口径） */
export interface WaitSources {
  /** 响应体 data.retry_after_seconds（整数秒） */
  seconds?: unknown
  /** 响应头 Retry-After（秒数或 HTTP 日期） */
  retryAfter?: string | null
  /** 响应体 data.locked_until（时刻串；只有锁定类错误才有） */
  until?: unknown
}

/**
 * 剩余等待秒数：优先 data.retry_after_seconds，其次 Retry-After 头，其次 locked_until − now；
 * 都没有（或都不合法）返回 null。结果向上取整、不为负。
 */
export function resolveWaitSeconds(src: WaitSources, now: number = serverNow()): number | null {
  const s = src.seconds
  if (typeof s === 'number' && Number.isFinite(s) && s >= 0) return Math.ceil(s)
  const fromHeader = parseRetryAfter(src.retryAfter, now)
  if (fromHeader !== null) return fromHeader
  const until = parseInstant(src.until ?? null)
  if (until === null) return null
  return Math.max(0, Math.ceil((until - now) / SECOND_MS))
}

/**
 * 等待时长 → 时 / 分，用于「请 1 小时 58 分后再试」。分钟**向上取整**（宁可多说，
 * 不让玩家按提示提前重试又被拒）；不足 1 分钟返回 null，文案改说「请稍后再试」。
 */
export function waitDuration(seconds: number): { hours: number; minutes: number } | null {
  if (!Number.isFinite(seconds) || seconds < 60) return null
  const total = Math.ceil(seconds / 60)
  return { hours: Math.floor(total / 60), minutes: total % 60 }
}

// ============================================================================
// 展示格式（玩家端：设备本地时区；tz 缺省 = 运行环境默认时区，只在客户端调用）
// ============================================================================

/** vue-i18n locale → 格式语言 */
export function timeLocaleOf(locale: string | undefined | null): TimeLocale {
  return typeof locale === 'string' && locale.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

/** 英文月份名（1–12）；long=true 为全称 */
export function monthNameEn(month: number, long = false): string {
  const list = long ? MONTHS_EN_LONG : MONTHS_EN
  return list[month - 1] ?? ''
}

/** 英文星期缩写（ISO：1=Mon … 7=Sun） */
export function weekdayNameEn(isoDay: number): string {
  return WEEKDAYS_EN[isoDay - 1] ?? ''
}

/** 详情格式：2026-09-27 22:30:15 */
export function formatDetail(ms: number, tz?: string): string {
  const p = toLocal(ms, tz)
  return `${fmtYmd(p.year, p.month, p.day)} ${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
}

/** 本地日期 + 时刻（到分钟）：2026-09-27 22:30 */
export function formatDateTime(ms: number, tz?: string): string {
  const p = toLocal(ms, tz)
  return `${fmtYmd(p.year, p.month, p.day)} ${pad(p.hour)}:${pad(p.minute)}`
}

/** 本地钟点：22:30:15 */
export function formatClock(ms: number, tz?: string): string {
  const p = toLocal(ms, tz)
  return `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
}

/** 本地钟点（到分钟）：22:30 */
export function formatHm(ms: number, tz?: string): string {
  const p = toLocal(ms, tz)
  return `${pad(p.hour)}:${pad(p.minute)}`
}

export interface SmartFormatOptions {
  /** 显示时区；缺省 = 运行环境默认时区 */
  tz?: string
  /** 「今天」的参照时刻（服务端校正后的 now） */
  now: number
  locale?: TimeLocale
  /** 「今天」「昨天」文案（来自 i18n common.time.today / yesterday），缺省用内置中英文 */
  today?: string
  yesterday?: string
}

/**
 * 列表智能格式（按日历判断，不按 24 小时）：
 *   中文 今天 22:30 / 昨天 22:30 / 09-25 22:30 / 跨年 2025-12-31 22:30
 *   英文 Today 22:30 / Yesterday 22:30 / Sep 25, 22:30 / 跨年 Dec 31, 2025, 22:30
 */
export function formatSmart(ms: number, opts: SmartFormatOptions): string {
  const locale = opts.locale ?? 'zh'
  const t = toLocal(ms, opts.tz)
  const n = toLocal(opts.now, opts.tz)
  const hm = `${pad(t.hour)}:${pad(t.minute)}`
  const dayDiff = daysFromCivil(n.year, n.month, n.day) - daysFromCivil(t.year, t.month, t.day)
  if (dayDiff === 0) return `${opts.today ?? (locale === 'zh' ? '今天' : 'Today')} ${hm}`
  if (dayDiff === 1) return `${opts.yesterday ?? (locale === 'zh' ? '昨天' : 'Yesterday')} ${hm}`
  if (locale === 'zh') {
    return t.year === n.year
      ? `${pad(t.month)}-${pad(t.day)} ${hm}`
      : `${fmtYmd(t.year, t.month, t.day)} ${hm}`
  }
  const md = `${monthNameEn(t.month)} ${t.day}`
  return t.year === n.year ? `${md}, ${hm}` : `${md}, ${t.year}, ${hm}`
}

/** 倒计时分量：秒向上取整（到点时恰好归零） */
export function countdownParts(ms: number): {
  days: number
  hours: number
  minutes: number
  seconds: number
  totalSeconds: number
} {
  const totalSeconds = Math.max(0, Math.ceil(ms / SECOND_MS))
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return { days, hours, minutes, seconds, totalSeconds }
}

export interface CountdownFormatOptions {
  locale?: TimeLocale
  /** 天数文案（来自 i18n common.time.days），缺省：中文「N天」、英文「Nd」 */
  days?: (n: number) => string
}

/** 倒计时文案：HH:MM:SS；满一天时前缀天数（1天 02:03:04 / 1d 02:03:04） */
export function formatCountdown(ms: number, opts: CountdownFormatOptions = {}): string {
  const p = countdownParts(ms)
  const hms = `${pad(p.hours)}:${pad(p.minutes)}:${pad(p.seconds)}`
  if (p.days === 0) return hms
  const dayText = opts.days
    ? opts.days(p.days)
    : (opts.locale ?? 'zh') === 'zh'
      ? `${p.days}天`
      : `${p.days}d`
  return `${dayText} ${hms}`
}
